import React, { useId } from "react";
import CategoryIcon from "./CategoryIcon";

/**
 * HGCategoryArt — Phase 2. Premium branded Hidden Gem artwork for the LARGE
 * category cards (landing, hero grids). Every category shares the same
 * gem-inspired frame (consistent visual system) but carries its own accent
 * hue + category glyph so the 27 categories feel like ONE family while
 * remaining instantly distinguishable.
 *
 * Design system:
 *   • White primary glyph
 *   • Pink brand facet on the top-left shard
 *   • Per-category accent hue on the lower shard
 *   • Subtle ambient glow (radial) behind the gem
 *   • Faceted diamond silhouette (hexagonal gem cut)
 *   • Ambient shimmer on the top-right facet
 *
 * NOTE: For small chips/filters/dense UI keep using <CategoryIcon /> (Phase 1)
 * — those need to stay legible at 12–16px. HGCategoryArt starts looking good
 * from ~56px and up.
 */

// Per-category accent hue for the lower shard. Curated to complement the
// dark Hidden Gem palette; NOT the same as the CSS gradient on the card,
// which lives in Landing.jsx.
const ACCENTS = {
  street_food:    "#FF6BA3",   // pink
  bbq:            "#FF6B35",   // ember
  seafood:        "#22D3EE",   // cyan
  mexican:        "#FF4D4D",   // chili red
  soul_food:      "#F59E0B",   // amber
  desserts:       "#F472B6",   // rose
  drinks:         "#38BDF8",   // sky
  snacks:         "#FB923C",   // orange
  plates:         "#34D399",   // emerald
  meal_prep:      "#84CC16",   // lime
  pop_ups:        "#A78BFA",   // violet
  cultural:       "#818CF8",   // indigo
  wholesale:      "#94A3B8",   // slate
  food_trucks:    "#F472B6",   // rose
  food_carts:     "#FCD34D",   // yellow
  fundraisers:    "#FB7185",   // pink-red
  catering:       "#C084FC",   // lilac
  home_bakery:    "#FBBF24",   // butter
  vegan:          "#4ADE80",   // green
  breakfast:      "#FDE047",   // yolk
  family_owned:   "#F87171",   // warm red
  late_night:     "#7DD3FC",   // moonlight
  homemade:       "#FDBA74",   // hearth
  wings:          "#F97316",   // buffalo orange
  coffee:         "#B45309",   // espresso
  farmers_market: "#65A30D",   // farm green
  hibachi:        "#EF4444",   // fire red
};

export default function HGCategoryArt({ slug, size = 96, className = "" }) {
  const accent = ACCENTS[slug] || "#FF6BA3";
  // Unique gradient IDs — required when the same SVG is rendered multiple
  // times on the page (categories grid) so <defs> don't collide.
  const uid = useId().replace(/[:]/g, "");
  const glowId  = `hgc-glow-${uid}`;
  const shineId = `hgc-shine-${uid}`;
  const shardId = `hgc-shard-${uid}`;
  const pinkId  = `hgc-pink-${uid}`;

  return (
    <div
      className={`relative inline-grid place-items-center ${className}`}
      style={{ width: size, height: size }}
      data-testid={`hg-category-art-${slug || "unknown"}`}
    >
      <svg
        viewBox="0 0 120 120"
        width={size}
        height={size}
        className="absolute inset-0"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={glowId} cx="50%" cy="46%" r="55%">
            <stop offset="0%"  stopColor={accent} stopOpacity="0.38" />
            <stop offset="70%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
          <linearGradient id={shineId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor="rgba(255,255,255,0.32)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          <linearGradient id={shardId} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%"  stopColor={accent} stopOpacity="0.45" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.08" />
          </linearGradient>
          <linearGradient id={pinkId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor="#FF6BA3" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#FF6BA3" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Ambient glow halo */}
        <circle cx="60" cy="56" r="58" fill={`url(#${glowId})`} />

        {/* Gem silhouette — hexagonal cut */}
        <path
          d="M60 10 L100 34 L94 92 L60 112 L26 92 L20 34 Z"
          fill="rgba(255,255,255,0.05)"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />

        {/* Lower right shard — accent tint */}
        <path
          d="M60 60 L94 92 L60 112 Z"
          fill={`url(#${shardId})`}
        />

        {/* Top-left brand shard — pink */}
        <path
          d="M60 10 L20 34 L60 60 Z"
          fill={`url(#${pinkId})`}
        />

        {/* Top-right shimmer facet */}
        <path
          d="M60 10 L100 34 L60 60 Z"
          fill={`url(#${shineId})`}
        />

        {/* Facet lines radiating from the crown */}
        <g stroke="rgba(255,255,255,0.22)" strokeWidth="0.6" fill="none">
          <path d="M60 10 L60 112" />
          <path d="M20 34 L100 34" />
          <path d="M20 34 L94 92" />
          <path d="M100 34 L26 92" />
        </g>

        {/* Crown highlight */}
        <path
          d="M46 22 L74 22"
          stroke="rgba(255,255,255,0.6)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>

      {/* Category glyph (Phase 1 lucide icon) centered on top of the gem */}
      <span
        className="relative z-[1] text-white"
        style={{
          filter: `drop-shadow(0 0 6px ${accent}66) drop-shadow(0 0 2px rgba(255,255,255,0.7))`,
        }}
      >
        <CategoryIcon
          slug={slug}
          size={Math.round(size * 0.36)}
          strokeWidth={1.7}
        />
      </span>
    </div>
  );
}
